import type { ConnectionScope } from "@typedoc/contracts/connections";
import type { People } from "../people";

/** Who owns what: a member's user ID to their name, as the Members read gives them. */
export type Owners = ReadonlyMap<string, string>;

/**
 * The owners the people read knows. A member whose user ID is empty came from an identity service
 * that predates the field; a connection's owner is never empty, so such a member names nobody.
 */
export function ownersFrom(people: Pick<People, "members"> | undefined): Owners {
  const owners = new Map<string, string>();
  for (const member of people?.members ?? []) {
    if (member.userId) owners.set(member.userId, member.name);
  }
  return owners;
}

/**
 * Whether the people read can name every owner: it has loaded and no member lacks a user ID. One
 * that does came from an identity service that predates the field, so an unmatched owner may be a
 * current member we cannot match, and must read "Owner unavailable", never "Former member".
 */
export function ownersComplete(people: Pick<People, "members"> | undefined): boolean {
  return people !== undefined && people.members.every((member) => member.userId !== "");
}

/** What an account says about whose it is: enough to choose a connection's Owner cell. */
export type Ownership = { scope: ConnectionScope; mine: boolean; ownerUserId: string | null };

/**
 * The Owner column's cell: who, in words, and which look it takes. "former" is an owner a loaded
 * people read does not list; "unknown" is one we cannot tell about because that read has not
 * loaded, so the screen never calls someone a former member on the strength of an error.
 */
export type OwnerCell = {
  kind: "you" | "workspace" | "member" | "former" | "unknown";
  label: string;
};

/**
 * A workspace account is the Workspace's whoever connected it; a personal one is "You", the
 * member's name, "Former member" or, when `known` is false (the people read failed), "Owner
 * unavailable".
 */
export function ownerCell(owner: Ownership, owners: Owners, known = true): OwnerCell {
  if (owner.mine) return { kind: "you", label: "You" };
  if (owner.scope === "workspace" || owner.ownerUserId === null) {
    return { kind: "workspace", label: "Workspace" };
  }
  const name = owners.get(owner.ownerUserId);
  if (name !== undefined) return { kind: "member", label: name };
  return known
    ? { kind: "former", label: "Former member" }
    : { kind: "unknown", label: "Owner unavailable" };
}

/** A website login carries no `mine`: it is the caller's when it is personal and theirs. */
export function websiteLoginOwner(
  login: { scope: ConnectionScope; ownerUserId: string | null },
  userId: string,
): Ownership {
  return {
    scope: login.scope,
    mine: login.scope === "personal" && login.ownerUserId === userId,
    ownerUserId: login.ownerUserId,
  };
}

/** Someone else's personal account: shown only when Other members is chosen, and read-only. */
export const belongsToOthers = (owner: Pick<Ownership, "scope" | "mine">) =>
  owner.scope === "personal" && !owner.mine;

/** The Owner menu's choices: the workspace's accounts, yours, and other members' personal ones. */
export type OwnerKey = "workspace" | "mine" | "others";

/** Which Owner choice an account falls under. A workspace account is the workspace's, whoever connected it. */
export const ownerKeyOf = (owner: Pick<Ownership, "scope" | "mine">): OwnerKey =>
  owner.scope === "workspace" ? "workspace" : owner.mine ? "mine" : "others";
